import { useEffect, useState } from "react";
import listblackicon from "../../../../assets/images/list-black.svg";
import useDomesticHelperFlow from "../../../../hooks/useDomesticHelperFlowSimple";
import useDomesticHelperFormData from "../../../../hooks/useDomesticHelperFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import SingleDatePicker from "../../../common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../common/tabs/formTabs";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import DomesticHelperBottomStickyBtnLayout from "../../layouts/DomesticHelperBottomStickyBtnLayout";
import DomesticHelperJourneyLayout from "../../layouts/DomesticHelperJourneyLayout";
import DomesticHelperBenefitsModal from "../../modals/domesticHelperBenefitsModal";
import "./applicationDetails.css";

export const DOMESTIC_HELPER_APPLICATION_DETAILS_FORM =
  "DOMESTIC_HELPER_APPLICATION_DETAILS_FORM";

const ApplicationDetails = () => {
  const translate = useLanguageTranslate();
  const { handleNextStep, handlePrevSubstep } = useDomesticHelperFlow();

  // Initialize form data state
  const [formData, setFormData] = useState({
    applicationType: "", // No default selection
    employmentType: "", // No default selection
    limitForAbsence: "", // No default selection
    policyPeriod: "", // No default selection (hidden for Interim)
    estimatedArrivalDate: "", // For "New" application type
    effectiveStartDate: "", // For "Visa Renewal" application type
    policyInceptionDate: "", // For "Interim" application type
    workPermitExpiryDate: "", // Always shown
  });

  const [showBenefitsModal, setShowBenefitsModal] = useState(false);

  const { saveDomesticHelperDataInRedux, getDomesticHelperFormData } =
    useDomesticHelperFormData(DOMESTIC_HELPER_APPLICATION_DETAILS_FORM);

  // Load saved form data on component mount
  useEffect(() => {
    const savedFormData = getDomesticHelperFormData();
    if (savedFormData) {
      const processedData = {
        applicationType: savedFormData.applicationType || "",
        employmentType: savedFormData.employmentType || "",
        limitForAbsence: savedFormData.limitForAbsence || "",
        policyPeriod: savedFormData.policyPeriod || "",
        estimatedArrivalDate: savedFormData.estimatedArrivalDate || "",
        effectiveStartDate: savedFormData.effectiveStartDate || "",
        policyInceptionDate: savedFormData.policyInceptionDate || "",
        workPermitExpiryDate: savedFormData.workPermitExpiryDate || "",
      };
      setFormData(processedData);
    }
  }, []);

  const [errors, setErrors] = useState({});

  // Application Type options
  const applicationTypeTabs = [
    {
      type: "new",
      label_en: "New",
      label_ar: "جديد",
    },
    {
      type: "visa_renewal",
      label_en: "Visa Renewal",
      label_ar: "تجديد التأشيرة",
    },
    {
      type: "interim",
      label_en: "Interim",
      label_ar: "مؤقت",
    },
  ];

  // Employment Type options
  const employmentTypeTabs = [
    {
      type: "direct_sourcing",
      label_en: "Direct Sourcing",
      label_ar: "التوريد المباشر",
    },
    {
      type: "visa_employment_office",
      label_en: "Visa Employment Office",
      label_ar: "مكتب تأشيرات العمل",
    },
  ];

  // Limit for absence of work options
  const limitForAbsenceTabs = [
    {
      type: "bhd_500",
      label_en: "BHD 500",
      label_ar: "500 د.ب",
    },
    {
      type: "bhd_1000",
      label_en: "BHD 1,000",
      label_ar: "1,000 د.ب",
    },
    {
      type: "bhd_1500",
      label_en: "BHD 1,500",
      label_ar: "1,500 د.ب",
    },
  ];

  // Policy Period options
  const policyPeriodTabs = [
    {
      type: "1_year",
      label_en: "1 Year",
      label_ar: "سنة واحدة",
    },
    {
      type: "2_years",
      label_en: "2 Years",
      label_ar: "سنتان",
    },
  ];

  const handleApplicationTypeChange = (applicationType) => {
    setFormData((prev) => ({
      ...prev,
      applicationType,
      // Clear all date fields when changing application type
      estimatedArrivalDate: "",
      effectiveStartDate: "",
      policyInceptionDate: "",
      // Clear policy period if switching to Interim
      policyPeriod: applicationType === "interim" ? "" : prev.policyPeriod,
    }));

    // Clear errors for date fields when user changes application type
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.applicationType;
      delete newErrors.estimatedArrivalDate;
      delete newErrors.effectiveStartDate;
      delete newErrors.policyInceptionDate;
      if (applicationType === "interim") {
        delete newErrors.policyPeriod;
      }
      return newErrors;
    });
  };

  const handleEmploymentTypeChange = (employmentType) => {
    setFormData((prev) => ({
      ...prev,
      employmentType,
    }));

    // Clear error when user makes selection
    if (errors.employmentType) {
      setErrors((prev) => ({ ...prev, employmentType: "" }));
    }
  };

  const handleLimitForAbsenceChange = (limitForAbsence) => {
    setFormData((prev) => ({
      ...prev,
      limitForAbsence,
    }));

    // Clear error when user makes selection
    if (errors.limitForAbsence) {
      setErrors((prev) => ({ ...prev, limitForAbsence: "" }));
    }
  };

  const handlePolicyPeriodChange = (policyPeriod) => {
    // Auto-calculate work permit expiry date based on policy period
    const today = new Date();
    let autoExpiryDate = "";

    if (policyPeriod === "1_year") {
      // Set to 1 year from today
      const futureDate = new Date(today);
      futureDate.setFullYear(futureDate.getFullYear() + 1);
      autoExpiryDate = futureDate.toISOString().split("T")[0];
    } else if (policyPeriod === "2_years") {
      // Set to 2 years from today
      const futureDate = new Date(today);
      futureDate.setFullYear(futureDate.getFullYear() + 2);
      autoExpiryDate = futureDate.toISOString().split("T")[0];
    }

    setFormData((prev) => ({
      ...prev,
      policyPeriod,
      // Auto-fill work permit expiry date based on policy period
      workPermitExpiryDate: autoExpiryDate,
    }));

    // Clear errors when user makes selection
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.policyPeriod;
      delete newErrors.workPermitExpiryDate;
      return newErrors;
    });
  };

  const handleEstimatedArrivalDateChange = (isoDateString) => {
    setFormData((prev) => ({
      ...prev,
      estimatedArrivalDate: isoDateString,
    }));

    // Clear error when user selects date
    if (errors.estimatedArrivalDate) {
      setErrors((prev) => ({ ...prev, estimatedArrivalDate: "" }));
    }
  };

  const handleEffectiveStartDateChange = (isoDateString) => {
    setFormData((prev) => ({
      ...prev,
      effectiveStartDate: isoDateString,
    }));

    // Clear error when user selects date
    if (errors.effectiveStartDate) {
      setErrors((prev) => ({ ...prev, effectiveStartDate: "" }));
    }
  };

  const handlePolicyInceptionDateChange = (isoDateString) => {
    setFormData((prev) => ({
      ...prev,
      policyInceptionDate: isoDateString,
    }));

    // Clear error when user selects date
    if (errors.policyInceptionDate) {
      setErrors((prev) => ({ ...prev, policyInceptionDate: "" }));
    }
  };

  const handleWorkPermitExpiryDateChange = (isoDateString) => {
    setFormData((prev) => ({
      ...prev,
      workPermitExpiryDate: isoDateString,
    }));

    // Clear error when user selects date
    if (errors.workPermitExpiryDate) {
      setErrors((prev) => ({ ...prev, workPermitExpiryDate: "" }));
    }
  };

  const handleViewBenefits = () => {
    setShowBenefitsModal(true);
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.applicationType) {
      newErrors.applicationType = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    if (!formData.employmentType) {
      newErrors.employmentType = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    if (!formData.limitForAbsence) {
      newErrors.limitForAbsence = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Policy Period is only required for "new" and "visa_renewal"
    if (formData.applicationType !== "interim" && !formData.policyPeriod) {
      newErrors.policyPeriod = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Validate date fields based on application type
    if (formData.applicationType === "new") {
      if (!formData.estimatedArrivalDate) {
        newErrors.estimatedArrivalDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }
    } else if (formData.applicationType === "visa_renewal") {
      if (!formData.effectiveStartDate) {
        newErrors.effectiveStartDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }
    } else if (formData.applicationType === "interim") {
      if (!formData.policyInceptionDate) {
        newErrors.policyInceptionDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }
    }

    // Work permit expiry date is always required
    if (!formData.workPermitExpiryDate) {
      newErrors.workPermitExpiryDate = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Business rule: Work permit expiry date validation based on policy period and application type
    if (formData.workPermitExpiryDate) {
      const selectedDate = new Date(formData.workPermitExpiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      // For interim applications, validate against policy inception date
      if (
        formData.applicationType === "interim" &&
        formData.policyInceptionDate
      ) {
        const inceptionDate = new Date(formData.policyInceptionDate);
        inceptionDate.setHours(0, 0, 0, 0);

        // Work permit expiry date must be later than the inception date
        if (selectedDate <= inceptionDate) {
          newErrors.workPermitExpiryDate = translate(
            "The work permit expiry date should be later than the inception date.",
            "يجب أن يتجاوز تاريخ انتهاء تصريح العمل تاريخ إصدار الوثيقة"
          );
        }
      } else {
        // For other application types, check if date is in the past
        if (selectedDate < today) {
          newErrors.workPermitExpiryDate = translate(
            "Work permit expiry date cannot be in the past",
            "لا يمكن أن يكون تاريخ انتهاء تصريح العمل في الماضي"
          );
        }
      }

      // Check if work permit is valid for the entire policy period (not applicable for interim)
      if (
        formData.policyPeriod &&
        formData.applicationType !== "interim" &&
        !newErrors.workPermitExpiryDate
      ) {
        let requiredValidityDate = new Date();

        if (formData.policyPeriod === "1_year") {
          // Work permit must be valid for at least 1 year from today
          requiredValidityDate.setFullYear(
            requiredValidityDate.getFullYear() + 1
          );
        } else if (formData.policyPeriod === "2_years") {
          // Work permit must be valid for at least 2 years from today
          requiredValidityDate.setFullYear(
            requiredValidityDate.getFullYear() + 2
          );
        }

        requiredValidityDate.setHours(0, 0, 0, 0);

        if (selectedDate < requiredValidityDate) {
          const periodText =
            formData.policyPeriod === "1_year"
              ? translate("1 year", "سنة واحدة")
              : translate("2 years", "سنتان");
          newErrors.workPermitExpiryDate = translate(
            `Work permit must be valid for the entire policy period (${periodText})`,
            `يجب أن يكون تصريح العمل صالحًا طوال فترة الوثيقة (${periodText})`
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      try {
        // Save all form data to Redux
        saveDomesticHelperDataInRedux(formData);
        handleNextStep();
      } catch (error) {
        console.error("Error saving application details:", error);
      }
    }
  };

  const isContinueDisabled = () => {
    // Always check these required fields
    if (
      !formData.applicationType ||
      !formData.employmentType ||
      !formData.limitForAbsence ||
      !formData.workPermitExpiryDate
    ) {
      return true;
    }

    // Check policy period (not required for interim)
    if (formData.applicationType !== "interim" && !formData.policyPeriod) {
      return true;
    }

    // Check date fields based on application type
    if (formData.applicationType === "new" && !formData.estimatedArrivalDate) {
      return true;
    }

    if (
      formData.applicationType === "visa_renewal" &&
      !formData.effectiveStartDate
    ) {
      return true;
    }

    if (
      formData.applicationType === "interim" &&
      !formData.policyInceptionDate
    ) {
      return true;
    }

    return false;
  };

  return (
    <div className="w-full application-details">
      <DomesticHelperJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <DomesticHelperBottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={isContinueDisabled()}
          btn_label={translate("Continue", "التالي")}
        >
          <div className="flex justify-center items-start min-h-[60vh] pb-10">
            <div className="w-full max-w-[600px] mx-auto px-4">
              <TitleDescription
                title={translate("Application Details", "تفاصيل الطلب")}
                description={translate(
                  "Choose the appropriate details for your application.",
                  "اختر التفاصيل المناسبة لطلبك."
                )}
              />

              <div className="space-y-6 mt-8">
                {/* Application Type */}
                <div className="w-full">
                  <FormTabs
                    label={<>{translate("Application Type", "نوع الطلب")}</>}
                    tabs={applicationTypeTabs}
                    activeTab={formData.applicationType}
                    onTabClick={handleApplicationTypeChange}
                    error={errors.applicationType}
                  />
                </div>

                {/* Employment Type */}
                <div className="w-full">
                  <FormTabs
                    label={<>{translate("Employment Type", "نوع العمل")}</>}
                    tabs={employmentTypeTabs}
                    activeTab={formData.employmentType}
                    onTabClick={handleEmploymentTypeChange}
                    error={errors.employmentType}
                  />
                </div>

                {/* Limit for absence of work */}
                <div className="w-full">
                  <FormTabs
                    label={translate(
                      "Limit for absence of work",
                      "مبلغ الحد الأقصى للغياب عن العمل"
                    )}
                    tabs={limitForAbsenceTabs}
                    activeTab={formData.limitForAbsence}
                    onTabClick={handleLimitForAbsenceChange}
                    error={errors.limitForAbsence}
                  />
                </div>

                {/* Show additional fields based ONLY on Application Type selection */}
                {formData.applicationType && (
                  <>
                    {/* Policy Period - Hidden for Interim */}
                    {formData.applicationType !== "interim" && (
                      <div className="w-full">
                        <FormTabs
                          label={translate("Policy Period", "مدة الوثيقة")}
                          tabs={policyPeriodTabs}
                          activeTab={formData.policyPeriod}
                          onTabClick={handlePolicyPeriodChange}
                          error={errors.policyPeriod}
                        />
                      </div>
                    )}

                    {/* Conditional Date Fields Based on Application Type */}
                    {formData.applicationType === "new" && (
                      <div className="w-full">
                        <SingleDatePicker
                          label={translate(
                            "Estimated arrival Date",
                            "تاريخ الوصول المتوقع"
                          )}
                          value={formData.estimatedArrivalDate}
                          onChange={handleEstimatedArrivalDateChange}
                          placeholder={translate(
                            "Select a Date",
                            "اختر التاريخ"
                          )}
                          error={errors.estimatedArrivalDate}
                          minDate={new Date()}
                          maxDate={
                            (() => {
                              const maxDate = new Date();
                              maxDate.setMonth(maxDate.getMonth() + 6);
                              maxDate.setDate(maxDate.getDate() - 1);
                              return maxDate;
                            })()
                          }
                          name="estimatedArrivalDate"
                        />
                      </div>
                    )}

                    {formData.applicationType === "visa_renewal" && (
                      <div className="w-full">
                        <SingleDatePicker
                          label={translate(
                            "Effective Start Date Of The Renewed work permit",
                            "تاريخ بدء سريان تصريح العمل المُجدَّد"
                          )}
                          value={formData.effectiveStartDate}
                          onChange={handleEffectiveStartDateChange}
                          placeholder={translate(
                            "Select a Date",
                            "اختر التاريخ"
                          )}
                          error={errors.effectiveStartDate}
                          minDate={new Date()}
                          maxDate={
                            (() => {
                              const maxDate = new Date();
                              maxDate.setMonth(maxDate.getMonth() + 6);
                              maxDate.setDate(maxDate.getDate() - 1);
                              return maxDate;
                            })()
                          }
                          name="effectiveStartDate"
                        />
                      </div>
                    )}

                    {formData.applicationType === "interim" && (
                      <div className="w-full">
                        <SingleDatePicker
                          label={translate(
                            "Policy Inception Date",
                            "تاريخ إصدار الوثيقة"
                          )}
                          value={formData.policyInceptionDate}
                          onChange={handlePolicyInceptionDateChange}
                          placeholder={translate(
                            "Select a Date",
                            "اختر التاريخ"
                          )}
                          error={errors.policyInceptionDate}
                          minDate={new Date()}
                          maxDate={
                            (() => {
                              const maxDate = new Date();
                              maxDate.setMonth(maxDate.getMonth() + 6);
                              maxDate.setDate(maxDate.getDate() - 1);
                              return maxDate;
                            })()
                          }
                          name="policyInceptionDate"
                        />
                      </div>
                    )}

                    {/* Work Permit Expiry Date - Shown after base selections */}
                    <div className="w-full">
                      <SingleDatePicker
                        label={translate(
                          "Work permit expiry date",
                          "تاريخ انتهاء تصريح العمل"
                        )}
                        value={formData.workPermitExpiryDate}
                        onChange={handleWorkPermitExpiryDateChange}
                        placeholder={translate("Select a Date", "اختر التاريخ")}
                        error={errors.workPermitExpiryDate}
                        minDate={
                          // For interim applications, min date should be inception date + 1 day
                          formData.applicationType === "interim" &&
                          formData.policyInceptionDate
                            ? (() => {
                                const inceptionDate = new Date(
                                  formData.policyInceptionDate
                                );
                                inceptionDate.setDate(
                                  inceptionDate.getDate() + 1
                                );
                                return inceptionDate;
                              })()
                            : // If policy period is selected, set min date to cover the policy duration
                            formData.policyPeriod
                            ? (() => {
                                const minDate = new Date();
                                if (formData.policyPeriod === "1_year") {
                                  minDate.setFullYear(
                                    minDate.getFullYear() + 1
                                  );
                                } else if (
                                  formData.policyPeriod === "2_years"
                                ) {
                                  minDate.setFullYear(
                                    minDate.getFullYear() + 2
                                  );
                                }
                                return minDate;
                              })()
                            : new Date()
                        }
                        maxDate={
                          new Date(
                            new Date().setFullYear(new Date().getFullYear() + 5)
                          )
                        }
                        name="workPermitExpiryDate"
                      />
                    </div>
                  </>
                )}

                {/* Essential Care benefits & covers button */}
                <div className="flex justify-center mt-8">
                  <button
                    className="flex items-center gap-2 border bg-white px-4 py-2 rounded-[10px] border-solid border-[#DDE2E4] text-[#1A2024] text-sm not-italic font-semibold leading-6 tracking-[-0.084px] mb-4 rtl:flex-row-reverse"
                    onClick={handleViewBenefits}
                  >
                    <img src={listblackicon} alt="list icon" />
                    {translate(
                      "View full list of benefits & covers",
                      "عرض القائمة الكاملة للمزايا والتغطيات"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DomesticHelperBottomStickyBtnLayout>
      </DomesticHelperJourneyLayout>

      {/* Benefits Modal */}
      <DomesticHelperBenefitsModal
        show={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
      />
    </div>
  );
};

export default ApplicationDetails;
